import { useRef, useState, useLayoutEffect } from "react";
import { StickyContainer } from "../SharedUI";
import { useContainerDimensions } from "../../hooks/useContainerDimensions";
import styled from "@emotion/styled";
import { useSpring, animated } from "@react-spring/web";
import { fontSizes } from "../typography";
import {
  labelConfig,
  layoutConfig,
  reliableIconConfig,
  arrowConfig,
  animationConfig,
  numberConfig,
  spacingConfig,
  colorConfig,
} from "./mirroringConfig";
import reliableSvg from "../../assets/reliable.svg";

interface LabelProps {
  text: string;
  x: number;
  y: number;
  className: string;
  size: "narrow" | "wide";
  containerWidth: number;
}

const MirroringStickyContainer = styled(StickyContainer)`
  flex-direction: column;
  gap: 2em;
`;

const LabelColors = colorConfig.labelColors;

function Label({ text, x, y, className, size, containerWidth }: LabelProps) {
  const textRef = useRef<SVGTextElement>(null);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      setDimensions({
        width:
          bbox.width +
          (size === "wide"
            ? labelConfig.widePadding * 2
            : labelConfig.padding * 2),
        height: bbox.height + labelConfig.verticalPadding * 2,
      });
    }
  }, [text, size, containerWidth]);

  if (!dimensions) {
    return (
      <g transform={`translate(${x}, ${y})`} className={className}>
        <text
          ref={textRef}
          x="0"
          y="0"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: fontSizes.mirroringLabels,
            fontWeight: 600,
            fill: "white",
            textTransform: "uppercase",
          }}
        >
          {text}
        </text>
      </g>
    );
  }

  const backgroundColor = LabelColors[text as keyof typeof LabelColors];
  const borderRadius = dimensions.height / 2;

  return (
    <g
      transform={`translate(${x - dimensions.width / 2}, ${y})`}
      className={className}
    >
      <rect
        width={dimensions.width}
        height={dimensions.height}
        rx={borderRadius}
        ry={borderRadius}
        fill={backgroundColor}
      />
      <text
        ref={textRef}
        x={dimensions.width / 2}
        y={dimensions.height / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: fontSizes.mirroringLabels,
          fontWeight: 600,
          fill: "white",
          textTransform: "uppercase",
        }}
      >
        {text}
      </text>
    </g>
  );
}

interface VisualizationProps {
  currentStep: string;
}

export default function MirroringViz({ currentStep }: VisualizationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useContainerDimensions(containerRef);
  const size = dimensions.width < 700 ? "narrow" : "wide";

  let svgWidth = dimensions.width;
  let svgHeight = dimensions.height * layoutConfig.svgHeightRatio;

  let column1X = svgWidth * layoutConfig.columns.leftX;
  let column2X = svgWidth * layoutConfig.columns.centerX;
  let column3X = svgWidth * layoutConfig.columns.rightX;

  let row1Y =
    svgHeight *
    (size === "narrow"
      ? layoutConfig.rows.narrowLabelY
      : layoutConfig.rows.labelY);
  let row2Y = svgHeight * layoutConfig.rows.numberY;
  let row3Y = svgHeight * layoutConfig.rows.lineY;

  const isWeighted =
    currentStep === "weighting" || currentStep === "mirroring-outro";

  const lineAnimation = useSpring({
    y1: isWeighted
      ? row3Y + animationConfig.moveAmount + animationConfig.lineOffsetAmount
      : row3Y,
    y2: isWeighted
      ? row3Y - animationConfig.moveAmount - animationConfig.lineOffsetAmount
      : row3Y,
    config: { duration: animationConfig.duration },
  });

  const centerNumberAnimation = useSpring({
    number: isWeighted
      ? numberConfig.weightedCenter
      : numberConfig.defaultCenter,
    config: animationConfig.spring,
  });

  // Labels rendered as components so measurement runs on mount

  let vizTitle = {
    "": "Whose trade value do we trust?",
    "mirroring-intro": "Whose trade value do we trust?",
    average: "Whose trade value do we trust?",
    "methodology-intro":
      "Which country has lower discrepancies with their trading partners?",
    reliability:
      "Which country has lower discrepancies with their trading partners?",
    weighting: "Whose trade value gets more weight?",
    "mirroring-outro": "Whose trade value gets more weight?",
  };

  return (
    <MirroringStickyContainer ref={containerRef}>
      <h2
        style={{
          color: colorConfig.textDark,
          fontWeight: 600,
          padding: `0 ${column1X * spacingConfig.headerPaddingFactor}px`,
          margin: 0,
          textAlign: "center",
          fontSize: fontSizes.vizTitle2,
        }}
      >
        {vizTitle[currentStep as keyof typeof vizTitle]}
      </h2>
      <svg width={svgWidth} height={svgHeight}>
        <style>{`
          g.left-group, g.right-group {
            transition: transform ${animationConfig.cssTransition};
          }
        `}</style>
        <g
          className="left-group"
          transform={
            isWeighted ? `translate(0, ${animationConfig.moveAmount})` : ""
          }
        >
          {(currentStep === "reliability" ||
            currentStep === "weighting" ||
            currentStep === "mirroring-outro") && (
            <>
              <image
                href={reliableSvg}
                x={column1X + reliableIconConfig.offsetX}
                y={row1Y + reliableIconConfig.offsetY}
                width={reliableIconConfig.width}
                height={reliableIconConfig.height}
              />
              <text
                x={column1X + reliableIconConfig.labelOffsetX}
                y={row1Y + reliableIconConfig.labelOffsetY}
                fill={colorConfig.textDarkest}
                fontSize={fontSizes.reporterLabel}
                textAnchor="start"
                fontWeight={600}
              >
                <tspan x={column1X + reliableIconConfig.labelOffsetX} dy="0">
                  MORE RELIABLE
                </tspan>
                <tspan
                  x={column1X + reliableIconConfig.labelOffsetX}
                  dy={reliableIconConfig.labelLineHeight}
                >
                  REPORTER
                </tspan>
              </text>
            </>
          )}

          <Label
            text="Country A"
            x={column1X}
            y={row1Y}
            className="left"
            size={size}
            containerWidth={svgWidth}
          />
          <text
            x={column1X}
            y={row2Y}
            fill={colorConfig.numberValue}
            fontSize={fontSizes.mirroringNumbers}
            textAnchor="middle"
            dominantBaseline="middle"
            fontWeight={600}
          >
            $603M
          </text>
        </g>

        {currentStep === "weighting" || currentStep === "mirroring-outro" ? (
          size === "wide" ? (
            <Label
              text="Estimated Trade Value"
              x={column2X}
              y={row1Y}
              className="center"
              size={size}
              containerWidth={svgWidth}
            />
          ) : (
            <Label
              text="Est. Trade Value"
              x={column2X}
              y={row1Y}
              className="center"
              size={size}
              containerWidth={svgWidth}
            />
          )
        ) : (
          currentStep !== "mirroring-intro" && (
            <Label
              text="Average"
              x={column2X}
              y={row1Y}
              className="center"
              size={size}
              containerWidth={svgWidth}
            />
          )
        )}

        {currentStep === "weighting" || currentStep === "mirroring-outro" ? (
          <animated.text
            x={column2X}
            y={row2Y}
            fill={colorConfig.textDarkest}
            fontSize={fontSizes.mirroringNumbers}
            textAnchor="middle"
            dominantBaseline="middle"
            fontWeight={600}
          >
            {centerNumberAnimation.number.to(
              (n) =>
                `$${Math.max(numberConfig.weightedCenter, Math.round(n))}M`,
            )}
          </animated.text>
        ) : currentStep !== "mirroring-intro" ? (
          <animated.text
            x={column2X}
            y={row2Y}
            fill={colorConfig.textMedium}
            fontSize={fontSizes.mirroringNumbers}
            textAnchor="middle"
            dominantBaseline="middle"
            fontWeight={600}
            className="center"
          >
            {centerNumberAnimation.number.to(
              (n) =>
                `$${Math.max(numberConfig.weightedCenter, Math.round(n))}M`,
            )}
          </animated.text>
        ) : (
          <text
            x={column2X}
            y={row2Y}
            fill={colorConfig.textMedium}
            fontSize={fontSizes.title}
            textAnchor="middle"
            className="center"
            fontWeight={600}
          >
            ?
          </text>
        )}

        <g
          className="right-group"
          transform={
            isWeighted ? `translate(0, ${-animationConfig.moveAmount})` : ""
          }
        >
          <Label
            text="Country B"
            x={column3X}
            y={row1Y}
            className="right"
            size={size}
            containerWidth={svgWidth}
          />
          <text
            x={column3X}
            y={row2Y}
            fill={colorConfig.numberValue}
            fontSize={fontSizes.mirroringNumbers}
            textAnchor="middle"
            dominantBaseline="middle"
            fontWeight={600}
          >
            $1.1B
          </text>
        </g>
        <animated.line
          x1={column1X / 2}
          x2={svgWidth - column1X / 2}
          y1={lineAnimation.y1}
          y2={lineAnimation.y2}
          stroke={colorConfig.lineStroke}
          strokeWidth="3"
        />
        <polygon
          points={`${column2X},${row3Y} ${
            column2X - arrowConfig.horizontalSpread
          },${row3Y + arrowConfig.verticalOffset} ${
            column2X + arrowConfig.horizontalSpread
          },${row3Y + arrowConfig.verticalOffset}`}
          fill={colorConfig.lineStroke}
        ></polygon>
      </svg>

      <div style={{ height: spacingConfig.descriptionHeight }}>
        {(currentStep === "weighting" || currentStep === "mirroring-outro") && (
          <p
            style={{
              color: colorConfig.textMedium,
              textAlign: "center",
              padding: `0 ${column1X * spacingConfig.headerPaddingFactor}px`,
              margin: 0,
            }}
          >
            Countries with generally lower discrepancies from all their partners
            are estimated to be more reliable → their values end up being
            weighted more.
          </p>
        )}
      </div>
    </MirroringStickyContainer>
  );
}
